import type { MediaAssetDto } from '@/types/dtos/media/media.asset.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { ProcessingInsightDto } from '@/types/dtos/media/processing-insight.dto';
import type { TranslationKey } from '@/i18n/translation-key';
import {
  isPreprocessableMediaFileType,
  resolvePreviewAssetForControl,
} from '@/helpers/media-processing-insight.helper';
import { hasProcessingAdvisories } from '@/helpers/media-processing-policy-messages.helper';

export type MediaItemAggregateStatus =
  | 'no_upload'
  | 'requested'
  | 'processing'
  | 'processing_error'
  | 'pending_approval'
  | 'pending_approval_warning'
  | 'approved';

export type MediaItemStatusTone = 'active' | 'primary' | 'info' | 'warning' | 'success' | 'error';

export type MediaItemAggregateStatusResult = {
  status: MediaItemAggregateStatus;
  tone: MediaItemStatusTone;
  showSpinner: boolean;
  insight?: ProcessingInsightDto;
  /** Overrides the status-derived label (in-flight uploads are not processing). */
  labelKey?: TranslationKey;
};

/** Color used by slot / start-end flow media indicators. */
export type MediaListIndicatorColor = 'disabled' | 'success' | 'error' | 'warning' | 'info' | 'primary';

function getLatestAsset(media: MediaDto) {
  // An in-flight upload is not a version. After a reload it must not keep the row on the hourglass.
  const assets = (media.assets ?? []).filter((asset) => asset.uploadStatus !== 'uploading');
  if (assets.length === 0) {
    return undefined;
  }
  return [...assets].sort((a, b) => b.version - a.version)[0];
}

/** No finished version yet: a requested row stays requested, otherwise there is nothing to upload. */
function resolveMediaWithoutVersion(media: MediaDto): MediaItemAggregateStatusResult {
  if (media.isRequested === true) {
    return { status: 'requested', tone: 'info', showSpinner: false };
  }
  return { status: 'no_upload', tone: 'primary', showSpinner: false };
}

function isProcessingInProgress(insight: ProcessingInsightDto): boolean {
  if (insight.readiness === 'rejected' || insight.readiness === 'failed') {
    return false;
  }
  if (insight.readiness === 'ok' || insight.playoutReady) {
    return false;
  }
  if (insight.status === 'running') {
    return true;
  }
  return (
    insight.status === 'not_started' ||
    insight.status === 'queued' ||
    insight.status === 'pending' ||
    insight.status === 'inspecting' ||
    insight.status === 'planned' ||
    insight.readiness === 'processing' ||
    insight.readiness === 'not_started'
  );
}

/** Status / tone for a single asset version (expanded list icons). */
export function resolveMediaAssetStatus(asset: MediaAssetDto): MediaItemAggregateStatusResult {
  // In-flight direct upload — show hourglass before processingInsight exists.
  if (asset.uploadStatus === 'uploading') {
    return {
      status: 'processing',
      tone: 'info',
      showSpinner: true,
      labelKey: 'media.uploadStatus.uploading',
    };
  }

  // Direct-blob upload failed before preprocess — surface as error, not pending/processing.
  if (asset.uploadStatus === 'failed') {
    return {
      status: 'processing_error',
      tone: 'error',
      showSpinner: false,
    };
  }

  if (asset.isApproved) {
    const insight = asset.processingInsight;
    const limitedPlayback =
      !!insight && (insight.readiness === 'ok' || insight.playoutReady) && hasProcessingAdvisories(insight);
    return {
      status: 'approved',
      // Limited playback stays visible on the asset — warn, don't treat as clean success.
      tone: limitedPlayback ? 'warning' : 'success',
      showSpinner: false,
      insight,
    };
  }

  // Preprocessable upload before insight is hydrated — treat as processing so the status icon
  // does not briefly show the file-type / pending-approval state.
  if (isPreprocessableMediaFileType(asset.fileType) && !asset.processingInsight) {
    return {
      status: 'processing',
      tone: 'info',
      showSpinner: true,
    };
  }

  const insight = isPreprocessableMediaFileType(asset.fileType) ? asset.processingInsight : undefined;
  if (insight && insight.readiness !== 'not_applicable') {
    if (insight.readiness === 'rejected' || insight.readiness === 'failed') {
      return {
        status: 'processing_error',
        tone: 'error',
        showSpinner: false,
        insight,
      };
    }
    if (isProcessingInProgress(insight)) {
      return {
        status: 'processing',
        tone: 'info',
        showSpinner: true,
        insight,
      };
    }
    if ((insight.readiness === 'ok' || insight.playoutReady) && hasProcessingAdvisories(insight)) {
      return {
        status: 'pending_approval_warning',
        tone: 'warning',
        showSpinner: false,
        insight,
      };
    }
  }

  return {
    status: 'pending_approval',
    tone: 'primary',
    showSpinner: false,
    insight,
  };
}

export function resolveMediaItemAggregateStatus(media: MediaDto): MediaItemAggregateStatusResult {
  const latest = getLatestAsset(media);
  if (!latest) {
    return resolveMediaWithoutVersion(media);
  }

  return resolveMediaAssetStatus(latest);
}

/**
 * Playout/preview asset used for the collapsed row file-type icon (matches media-item selection).
 */
export function resolveMediaItemPlayoutAsset(media: MediaDto): MediaAssetDto | undefined {
  const assets = media.assets ?? [];
  if (assets.length === 0) {
    return undefined;
  }
  const preview = resolvePreviewAssetForControl(assets);
  if (preview) {
    return preview;
  }
  const approved = [...assets].filter((asset) => asset.isApproved).sort((a, b) => b.version - a.version)[0];
  if (approved) {
    return approved;
  }
  return getLatestAsset(media);
}

/**
 * Collapsed media-item / slot indicator status.
 * - Latest unapproved processing → processing spinner (even when an earlier approved asset exists)
 * - Latest unapproved processing_error → error, or warning if an earlier approved asset exists
 * - Latest unapproved pending_approval_warning → warning
 * - Latest unapproved pending_approval (processed, ready to approve) → primary (actionable)
 * - Otherwise playout/preview asset; approved + limited playback → success at item level (asset stays warn)
 *
 * Warn/error tones remain for slot list aggregation (`resolveMediaListIndicatorColor`).
 * The media-row file icon uses {@link resolveMediaItemRowProcessTone} instead (no warn/error colors).
 */
export function resolveMediaItemRowDisplayStatus(
  media: MediaDto,
  playoutAsset?: MediaAssetDto,
): MediaItemAggregateStatusResult {
  const latest = getLatestAsset(media);
  if (!latest) {
    return resolveMediaItemAggregateStatus(media);
  }

  const assets = media.assets ?? [];
  const latestStatus = resolveMediaAssetStatus(latest);

  if (!latest.isApproved) {
    // In-flight replacement must win over an older approved playout asset — otherwise the row
    // keeps showing the approved file/upload icon and the new version looks "invisible".
    if (latestStatus.status === 'processing') {
      return latestStatus;
    }
    if (latestStatus.status === 'processing_error') {
      const hasEarlierApproved = assets.some((asset) => asset.version < latest.version && asset.isApproved);
      if (hasEarlierApproved) {
        return { ...latestStatus, tone: 'warning' };
      }
      return latestStatus;
    }
    if (latestStatus.status === 'pending_approval_warning' || latestStatus.status === 'pending_approval') {
      return latestStatus;
    }
  }

  const baseAsset = playoutAsset ?? resolveMediaItemPlayoutAsset(media) ?? latest;
  const base = resolveMediaAssetStatus(baseAsset);
  if (base.status === 'approved' && base.tone === 'warning') {
    return { ...base, tone: 'success' };
  }
  return base;
}

/** Process colors for the media-row file/upload icon — warn/error live on the trailing affordance. */
export type MediaItemRowProcessTone = 'primary' | 'info' | 'success';

/**
 * Maps row status to process indication only:
 * - primary — organizer/contributor task (upload, approval, fix rejected upload)
 * - info — waiting (requested / processing)
 * - success — latest (or playout) asset approved
 */
export function resolveMediaItemRowProcessTone(
  result: MediaItemAggregateStatusResult,
  options?: { contributorFacing?: boolean; canUpload?: boolean },
): MediaItemRowProcessTone {
  switch (result.status) {
    case 'approved':
      return 'success';
    case 'requested':
      // Contributor still needs to upload → their task; manager waits on contributor → info.
      return options?.contributorFacing && options.canUpload ? 'primary' : 'info';
    case 'processing':
      return 'info';
    case 'no_upload':
    case 'pending_approval':
    case 'pending_approval_warning':
    case 'processing_error':
      return 'primary';
    default:
      return 'primary';
  }
}

/**
 * Worst-status color across media items for slot / start-end flow indicators.
 * Error and warning (from row display status) outrank requested (info) and pending approval (primary).
 */
export function resolveMediaListIndicatorColor(mediaList: MediaDto[]): MediaListIndicatorColor {
  if (mediaList.length === 0) {
    return 'disabled';
  }

  const rows = mediaList.map((media) => resolveMediaItemRowDisplayStatus(media));

  if (rows.some((row) => row.tone === 'error')) {
    return 'error';
  }
  if (rows.some((row) => row.tone === 'warning')) {
    return 'warning';
  }

  if (rows.every((row) => row.status === 'approved' && row.tone === 'success')) {
    return 'success';
  }

  const requestedMediaHasNoAssets = mediaList.some((media) => {
    const isRequested = media.isRequested === true;
    const hasNoAssets = !media.assets || media.assets.length === 0;
    return isRequested && hasNoAssets;
  });
  if (requestedMediaHasNoAssets) {
    return 'info';
  }

  if (rows.some((row) => row.status === 'pending_approval')) {
    return 'primary';
  }
  if (rows.some((row) => row.status === 'processing')) {
    return 'info';
  }

  const noMediaHasAssets = mediaList.every((media) => !media.assets || media.assets.length === 0);
  const hasRequestedMedia = mediaList.some((media) => media.isRequested === true);
  if (!hasRequestedMedia && noMediaHasAssets) {
    return 'primary';
  }

  return 'primary';
}

export function mapMediaItemStatusToneToIconColor(
  tone: MediaItemStatusTone,
): 'primary' | 'info' | 'warning' | 'success' | 'error' | 'disabled' | 'inherit' | undefined {
  switch (tone) {
    case 'active':
      return 'inherit';
    case 'primary':
      return 'primary';
    case 'info':
      return 'info';
    case 'warning':
      return 'warning';
    case 'success':
      return 'success';
    case 'error':
      return 'error';
    default:
      return undefined;
  }
}

export function resolveMediaStatusLabelKey(
  result: MediaItemAggregateStatusResult,
  options?: { contributorFacing?: boolean; canUpload?: boolean },
): TranslationKey {
  return result.labelKey ?? mediaItemStatusLabelKey(result.status, result.insight, options);
}

/** Human-readable status label key for media-item / asset status tooltips. */
export function mediaItemStatusLabelKey(
  status: MediaItemAggregateStatus,
  insight?: ProcessingInsightDto,
  options?: { contributorFacing?: boolean; canUpload?: boolean },
): TranslationKey {
  if (status === 'processing_error') {
    return insight?.readiness === 'rejected' ? 'media.processing.status.rejected' : 'media.processing.status.failed';
  }
  switch (status) {
    case 'no_upload':
      return options?.canUpload ? 'common.upload' : 'media.noAsset';
    case 'requested':
      // Contributor view: this row is waiting for their upload, not an organizer "request" badge.
      return options?.contributorFacing && options.canUpload ? 'common.upload' : 'media.requested';
    case 'processing':
      return 'media.processing.status.processing';
    case 'pending_approval':
      return 'media.pendingApproval';
    case 'pending_approval_warning':
      return 'media.processing.status.readyWithLimitations';
    case 'approved':
      return 'media.approved';
    default:
      return options?.canUpload ? 'common.upload' : 'media.noAsset';
  }
}

/** Contributor dashboard: primary for contributor tasks, warning for limited playback, info for wait states. */
export function resolveContributorFacingMediaItemTone(
  result: MediaItemAggregateStatusResult,
  options?: { canUpload?: boolean },
): MediaItemStatusTone {
  switch (result.status) {
    case 'requested':
      return 'primary';
    case 'no_upload':
      return options?.canUpload ? 'primary' : 'info';
    case 'processing':
    case 'pending_approval':
      return 'info';
    case 'pending_approval_warning':
      return 'warning';
    case 'approved':
      // Item-level approved is always success; limited playback warn stays on the asset icon.
      return 'success';
    case 'processing_error':
      // Downgraded error (earlier approved asset) keeps warning tone.
      return result.tone === 'warning' ? 'warning' : 'error';
    default:
      return result.tone;
  }
}
